/**
 * Finish — the reveal, the optional seal, its word, and the ready screen.
 *
 * The reveal is written after the clues it pays off, and the secret word comes
 * last because it is a seal on the envelope, not part of writing the letter.
 */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../chassis/Icon";
import { BigField, ChoiceRow, GhostPill, Heading, PrimaryButton, ProBadge, SecondaryButton, StepChip, Sub } from "../chassis/widgets";
import { C, FONT, SERIF, TINT } from "../chassis/tokens";
import { LIMITS, MIN_WORD_LENGTH } from "../constants";
import { completeClues } from "../state";
import { safeMediaUrl } from "../../../utils/safeMedia";
import { themedText } from "../themedCopy";

export function RevealScreen({ state, dispatch, isPro, onMediaLocked, onUploadMedia }) {
  const { t: tr } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const [kind, setKind] = useState("photo");
  const finaleSrc = state.finale
    ? safeMediaUrl(state.finale.url, { photo: "image", video: "video", song: "audio" }[state.finale.type])
    : "";

  async function pick(file) {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const url = await onUploadMedia(file, kind);
      dispatch({ type: "setFinale", value: { type: kind, url } });
    } catch (err) {
      setError(tr(`app.creator.media.errors.${(err && err.code) || "upload-failed"}`, { defaultValue: tr("app.creator.media.errors.upload-failed") }));
    } finally {
      setBusy(false);
    }
  }

  function choose(nextKind) {
    if (!isPro) return onMediaLocked();
    setKind(nextKind);
    // The ref click has to happen in the same tick as the user gesture on iOS.
    if (fileRef.current) {
      fileRef.current.accept = ACCEPT[nextKind];
      fileRef.current.click();
    }
  }

  return (
    <>
      <StepChip badge={<Icon name="sparkle" size={11} color={C.onAccent} fill={C.onAccent} />} label={tr("app.creator.reveal.eyebrow")} />
      <Heading accent={state.recipientName || null}>{tr("app.creator.reveal.heading")}</Heading>
      <Sub>{tr("app.creator.reveal.sub")}</Sub>

      <BigField
        id="creator-final-message"
        label={tr("app.creator.reveal.label")}
        value={state.finalMessage}
        onChange={(v) => dispatch({ type: "setFinalMessage", value: v })}
        placeholder={themedText(tr, state.theme, "reveal.placeholder")}
        multiline
        rows={5}
        fontSize={23}
        serif
        count={`${state.finalMessage.length} / ${LIMITS.finalMessage}`}
      />

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files && e.target.files[0];
          e.target.value = "";
          pick(file);
        }}
      />

      {state.finale ? (
        <div style={{ marginTop: 16, background: C.surface, borderRadius: 20, padding: 12, boxShadow: `0 12px 26px -20px ${TINT.shadow(0.35)}` }}>
          {!finaleSrc ? null : state.finale.type === "photo" ? (
            <img src={finaleSrc} alt="" style={{ display: "block", width: "100%", borderRadius: 14, maxHeight: 200, objectFit: "cover" }} />
          ) : state.finale.type === "video" ? (
            <video data-testid="creator-reveal-video-preview" controls playsInline preload="metadata" src={`${finaleSrc}#t=0.1`} style={{ display: "block", width: "100%", borderRadius: 14, maxHeight: 240, background: "#000" }} />
          ) : (
            <audio controls src={finaleSrc} style={{ width: "100%" }} />
          )}
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 10 }}>
            <GhostPill icon={MEDIA_ICON[state.finale.type] || "image"} onClick={() => choose(state.finale.type)}>{busy ? tr("app.creator.media.uploading") : tr("app.creator.reveal.replace")}</GhostPill>
            <GhostPill icon="trash" onClick={() => dispatch({ type: "setFinale", value: null })}>{tr("app.creator.reveal.remove")}</GhostPill>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 2px 9px" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.body }}>{tr("app.creator.reveal.attachLabel")}</span>
            {!isPro && <ProBadge />}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 9 }}>
            {["photo", "video", "song"].map((k) => (
              <button key={k} type="button" data-testid={`creator-reveal-${k}`} onClick={() => choose(k)} disabled={busy} style={attachStyle}>
                <Icon name={MEDIA_ICON[k]} size={19} color={C.label} strokeWidth={2.1} />
                {busy && kind === k ? tr("app.creator.media.uploading") : tr(`app.creator.reveal.kind.${k}`)}
              </button>
            ))}
          </div>
          {busy && kind === "video" ? <p style={{ margin: "10px 2px 0", fontSize: 12, color: C.faint }}>{tr("app.creator.media.videoWait")}</p> : null}
        </div>
      )}

      {error ? <p role="alert" style={{ margin: "14px 2px 0", fontSize: 13, fontWeight: 600, color: C.accentDeep }}>{error}</p> : null}
      <div style={{ height: 16 }} />
    </>
  );
}

const ACCEPT = { photo: "image/*", video: "video/*", song: "audio/*" };
const MEDIA_ICON = { photo: "image", video: "video", song: "music" };

const attachStyle = {
  minWidth: 0, height: 72, borderRadius: 18, border: `1.5px dashed ${C.dash}`, background: TINT.glass(0.55),
  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: "0 6px",
  fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.body, cursor: "pointer", textAlign: "center",
};

const ENVELOPE_SEALED = (
  <span style={{ position: "relative", flexShrink: 0, width: 72, height: 58, borderRadius: 14, background: "linear-gradient(160deg, #FFF3F4 0%, #F9DCE3 100%)", display: "grid", placeItems: "center" }}>
    <svg width="52" height="38" viewBox="0 0 52 38" aria-hidden="true">
      <rect x="1" y="3" width="50" height="34" rx="5" fill="#FFFFFF" stroke="#EBC3CF" strokeWidth="1.5" />
      <path d="M2 5 26 22 50 5" fill="none" stroke="#EBC3CF" strokeWidth="1.5" />
    </svg>
    <span style={{ position: "absolute", top: 20, left: 26, width: 22, height: 22, borderRadius: 999, background: "radial-gradient(circle at 35% 30%, #D8406E 0%, #A11E4E 80%)", boxShadow: "0 4px 8px -3px rgba(161,30,78,0.7)", display: "grid", placeItems: "center" }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="#FFFFFF" aria-hidden="true"><path d="M12 20.5s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.6a4.3 4.3 0 0 1 7.5 2.7c0 5.6-7.5 10.2-7.5 10.2z" /></svg>
    </span>
  </span>
);

const ENVELOPE_OPEN = (
  <span style={{ flexShrink: 0, width: 72, height: 58, borderRadius: 14, background: C.surfaceAlt, display: "grid", placeItems: "center" }}>
    <svg width="52" height="44" viewBox="0 0 52 44" aria-hidden="true">
      <path d="M1 18 26 2l25 16" fill="#FFFFFF" stroke="#D9C8CE" strokeWidth="1.5" strokeLinejoin="round" />
      <rect x="1" y="18" width="50" height="25" rx="5" fill="#FFFFFF" stroke="#D9C8CE" strokeWidth="1.5" />
      <path d="M2 20l24 14 24-14" fill="none" stroke="#D9C8CE" strokeWidth="1.5" />
    </svg>
  </span>
);

export function SealScreen({ state, dispatch }) {
  const { t: tr } = useTranslation();
  return (
    <>
      <StepChip badge={<Icon name="lock" size={11} color={C.onAccent} strokeWidth={2.8} />} label={tr("app.creator.seal.eyebrow")} />
      <Heading accent={tr("app.creator.seal.headingAccent")}>{tr("app.creator.seal.heading")}</Heading>
      <Sub>{tr("app.creator.seal.sub", { name: state.recipientName })}</Sub>

      <div style={{ marginTop: 26, display: "flex", flexDirection: "column", gap: 14 }}>
        <ChoiceRow
          id="creator-seal-yes"
          name="creator-seal"
          checked={state.seal.enabled === true}
          onChange={() => dispatch({ type: "setSealEnabled", value: true })}
          art={ENVELOPE_SEALED}
          title={tr("app.creator.seal.yes.title")}
          description={tr("app.creator.seal.yes.description", { name: state.recipientName })}
        />
        <ChoiceRow
          id="creator-seal-no"
          name="creator-seal"
          checked={state.seal.enabled === false}
          onChange={() => dispatch({ type: "setSealEnabled", value: false })}
          art={ENVELOPE_OPEN}
          title={tr("app.creator.seal.no.title")}
          description={tr("app.creator.seal.no.description")}
        />
      </div>
    </>
  );
}

export function WordScreen({ state, dispatch }) {
  const { t: tr } = useTranslation();
  const [showHint, setShowHint] = useState(!!state.seal.hint);
  const tooShort = state.seal.word.trim().length > 0 && state.seal.word.trim().length < MIN_WORD_LENGTH;
  return (
    <>
      <StepChip badge={<Icon name="lock" size={11} color={C.onAccent} strokeWidth={2.8} />} label={tr("app.creator.seal.eyebrow")} />
      <Heading accent={state.recipientName || null}>{tr("app.creator.word.heading")}</Heading>
      <Sub>{tr("app.creator.word.sub")}</Sub>

      <BigField
        id="creator-word"
        label={tr("app.creator.word.label")}
        value={state.seal.word}
        onChange={(v) => dispatch({ type: "setWord", value: v })}
        placeholder={themedText(tr, state.theme, "word.placeholder")}
        fontSize={28}
      />
      {tooShort ? (
        <p style={{ margin: "10px 4px 0", fontSize: 13, fontWeight: 600, color: C.accentDeep }}>{tr("app.creator.word.tooShort", { count: MIN_WORD_LENGTH })}</p>
      ) : null}

      <div style={{ marginTop: 18 }}>
        {showHint ? (
          <BigField
            id="creator-word-hint"
            label={tr("app.creator.word.hintLabel")}
            value={state.seal.hint}
            onChange={(v) => dispatch({ type: "setWordHint", value: v })}
            placeholder={themedText(tr, state.theme, "word.hintPlaceholder")}
            fontSize={18}
            autoFocus={false}
          />
        ) : (
          <GhostPill testId="creator-word-add-hint" onClick={() => setShowHint(true)}>{tr("app.creator.word.addHint")}</GhostPill>
        )}
      </div>
      <div style={{ height: 16 }} />
    </>
  );
}

export function ReadyScreen({ state, dispatch, defaultTitle, onPlayThrough, slotsNote }) {
  const { t: tr } = useTranslation();
  const written = completeClues(state).length;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div style={{ position: "relative", width: 200, height: 186, display: "grid", placeItems: "center" }}>
        <span style={{ position: "absolute", width: 186, height: 186, borderRadius: 999, border: `1.5px solid ${TINT.accent(0.14)}` }} />
        <span style={{ position: "absolute", width: 148, height: 148, borderRadius: 999, border: `1.5px solid ${TINT.accent(0.22)}`, background: TINT.glass(0.35) }} />
        <span style={{ position: "relative", width: 110, height: 110, borderRadius: 32, background: "linear-gradient(145deg, #FF8AB0 0%, #E0467A 52%, #B9245A 100%)", boxShadow: "0 24px 40px -16px rgba(185,36,90,0.7), inset 0 2px 0 rgba(255,255,255,0.35)", display: "grid", placeItems: "center", transform: "rotate(-6deg)" }}>
          <span style={{ fontSize: 56, fontWeight: 800, color: C.white, letterSpacing: "-0.04em" }}>P</span>
          <span style={{ position: "absolute", top: -12, right: -14, width: 42, height: 42, borderRadius: 999, background: "radial-gradient(circle at 35% 30%, #D8406E 0%, #9E1D4C 80%)", border: `3px solid ${C.white}`, boxShadow: "0 8px 14px -6px rgba(158,29,76,0.7)", display: "grid", placeItems: "center", transform: "rotate(14deg)" }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="#FFFFFF" aria-hidden="true"><path d="M12 20.5s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.6a4.3 4.3 0 0 1 7.5 2.7c0 5.6-7.5 10.2-7.5 10.2z" /></svg>
          </span>
        </span>
      </div>

      <div style={{ marginTop: 4, fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: C.accent }}>{tr("app.creator.ready.eyebrow")}</div>
      <Heading size={34} accent={state.recipientName || null}>{tr("app.creator.ready.heading")}</Heading>

      <div style={{ alignSelf: "stretch", marginTop: 20, background: C.surface, borderRadius: 22, padding: "14px 16px 14px 18px", boxShadow: `0 1px 2px ${TINT.shadow(0.05)}, 0 18px 36px -20px ${TINT.accent(0.35)}`, display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ flexGrow: 1, minWidth: 0 }}>
          <label htmlFor="creator-title" style={{ display: "block", fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: C.label }}>{tr("app.creator.ready.titleLabel")}</label>
          <input
            id="creator-title"
            type="text"
            value={state.title}
            placeholder={defaultTitle}
            onChange={(e) => dispatch({ type: "setTitle", value: e.target.value })}
            style={{ width: "100%", boxSizing: "border-box", marginTop: 3, border: "none", outline: "none", background: "transparent", padding: 0, fontFamily: FONT, fontSize: 19, fontWeight: 800, letterSpacing: "-0.015em", color: C.ink }}
          />
        </span>
        <span style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 12, background: C.accentSoft, display: "grid", placeItems: "center" }}>
          <Icon name="pencil" size={16} color={C.accent} strokeWidth={2.2} />
        </span>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8, marginTop: 14 }}>
        <span style={chipStyle}>{tr("app.creator.ready.clues", { count: written })}</span>
        <span style={chipStyle}>
          <Icon name={state.seal.enabled ? "lock" : "unlock"} size={11} color={C.muted} strokeWidth={2.6} />
          {state.seal.enabled ? tr("app.creator.ready.sealed") : tr("app.creator.ready.open")}
        </span>
        {state.finale ? (
          <span style={chipStyle}>
            <Icon name={MEDIA_ICON[state.finale.type] || "image"} size={11} color={C.muted} strokeWidth={2.6} />
            {tr(`app.creator.ready.${state.finale.type}`)}
          </span>
        ) : null}
      </div>

      <div style={{ alignSelf: "stretch", marginTop: 18 }}>
        <SecondaryButton testId="creator-play-through" icon="play" onClick={onPlayThrough}>
          {tr("app.creator.ready.playThrough")}
        </SecondaryButton>
      </div>
      {slotsNote ? <p style={{ margin: "14px 0 0", textAlign: "center", fontSize: 12, color: C.faint }}>{slotsNote}</p> : null}
      <div style={{ height: 8 }} />
    </div>
  );
}

const chipStyle = {
  display: "inline-flex", alignItems: "center", gap: 5, height: 32, padding: "0 12px", borderRadius: 999,
  background: TINT.glass(0.8), fontSize: 12, fontWeight: 700, color: C.muted,
};

export function readyFooter({ tr, onPublish, publishing }) {
  return (
    <PrimaryButton testId="creator-publish" onClick={onPublish} disabled={publishing} icon={null}>
      {publishing ? tr("app.creator.ready.creating") : tr("app.creator.ready.cta")}
    </PrimaryButton>
  );
}
